import React, { useEffect, useMemo, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Plus, Check, X, Library } from "lucide-react";
import { asArray } from "@/lib/asArray";

/**
 * Batch 0 Phase 4 — Universal "Add from library" popup.
 *
 * Any page in the audit workspace can trigger this dialog to add one
 * or more forms from the firm-wide library to the current engagement.
 * The heavy DocumentLibrary page still owns bulk-add and batch tab
 * operations; this popup is the light-weight, callable-from-anywhere
 * counterpart the Batch 0 spec calls out.
 *
 * Props:
 *  - open, onOpenChange   — controlled dialog visibility
 *  - onAdded              — invoked after each successful add with the form row
 *  - filterTabId          — optional tab_id to restrict the search
 *  - title / description  — override the default headings when embedded in a
 *                            specific section (e.g. "Add form to Head Folders")
 */
export default function AddFromLibraryDialog({
  open, onOpenChange, onAdded, filterTabId,
  title, description,
}) {
  const { t } = useI18n();
  const { currentEngId } = useEngagement();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (!open || !currentEngId) return;
    let alive = true;
    api.get(`/forms/${currentEngId}/document-library`, { _silent: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ forms: [] }); });
    return () => { alive = false; };
  }, [open, currentEngId]);

  const rows = useMemo(() => {
    const forms = asArray(data?.forms).filter((f) =>
      (!filterTabId || f.tab_id === filterTabId));
    const needle = q.trim().toLowerCase();
    return forms.filter((f) => {
      if (!needle) return true;
      return (f.ref_no || "").toLowerCase().includes(needle)
          || (f.title || "").toLowerCase().includes(needle);
    });
  }, [data, q, filterTabId]);

  const add = async (form) => {
    setPendingId(form.form_id);
    try {
      await api.post(`/forms/${currentEngId}/${form.form_id}/add-to-engagement`);
      toast.success(t("docLibExtras.addedToEng").replace("{ref}", form.ref_no));
      // Refresh local list so the row now shows "In engagement".
      const r = await api.get(`/forms/${currentEngId}/document-library`, { _silent: true });
      setData(r.data);
      onAdded?.(form);
    } catch (e) {
      toast.error(toErrMsg(e, t("docLibExtras.failedAdd")));
    } finally { setPendingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="add-from-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            {title || t("addFromLibrary.title")}
          </DialogTitle>
          {(description || filterTabId) && (
            <DialogDescription>
              {description || t("addFromLibrary.desc")}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute start-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("docLib.searchPlaceholder")}
            className="h-9 w-full ps-7 pe-6 text-sm"
            data-testid="add-from-library-search"
            autoFocus
          />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute end-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="add-from-library-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {!data && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t("docLib.loading")}
          </div>
        )}
        {data && rows.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground"
                data-testid="add-from-library-empty">
            {q ? t("docLibExtras.noMatch").replace("{q}", q)
               : t("addFromLibrary.empty")}
          </div>
        )}
        {data && rows.length > 0 && (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800"
               data-testid="add-from-library-list">
            {rows.slice(0, 200).map((f) => (
              <li key={f.form_id}
                  className="flex items-center gap-3 px-3 py-2 text-sm"
                  data-testid={`add-from-library-row-${f.form_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                      {f.ref_no}
                    </span>
                    <span className="break-words font-medium">{f.title}</span>
                  </div>
                  {f.tab_id && !filterTabId && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{f.tab_id}</div>
                  )}
                </div>
                {f.in_engagement ? (
                  <Badge variant="outline" className="gap-1 text-[10px]" data-testid={`add-from-library-in-${f.form_id}`}>
                    <Check className="h-3 w-3" /> {t("docLibExtras.inEngagement")}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 gap-1"
                          disabled={pendingId === f.form_id}
                          onClick={() => add(f)}
                          data-testid={`add-from-library-add-${f.form_id}`}>
                    <Plus className="h-3 w-3" />
                    {pendingId === f.form_id ? t("docLib.adding") : t("docLibExtras.addToEng")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
